import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@livecommerce/ui";
import { ApiError } from "../api/client";
import { type ShowOrderRow, shows } from "./api";
import styles from "./Shows.module.scss";

/**
 * Settlement, for the person running the show (spec §15.5, `R-SYNC-16`, `R-HOST-26`).
 *
 * ## What this is for, and what it is not
 *
 * It answers one question — *did everybody's order land* — and it exists because `R-SYNC-16`
 * calls the answer *no* an **incident**. The alarm on the settlement dead-letter queue is what
 * wakes somebody; this is what they read, and what a host checks at the end of a show without
 * having to open the Shopify admin and count.
 *
 * It is **not** the post-show summary `R-HOST-26` asks for in full. That wants what sold, what
 * did not, and gross revenue as well — the show's figures above this component cover those in
 * part, and the summary itself is M2.9's. What is here is the fourth thing that requirement asks
 * for: *anything requiring attention*.
 *
 * ## Why it is only shown on an ended show
 *
 * Because before the show ends there is nothing to be wrong with. A show order accumulating
 * during a live show has no Shopify order and is not supposed to, so a table of them all reading
 * *not settled* would be a screenful of alarming nothing on the one screen a host is using to
 * run a show. Spec §15.2's *it closes when the show ends* is the moment the question becomes
 * answerable, and this appears then.
 */
/** How often to look again while the queue still has something in it. */
const FOLLOW_MS = 2_500;

/**
 * How many times, before it stops being *in flight* and starts being *stuck*.
 *
 * Fifty seconds. A settlement is a queue hop and a Shopify round trip; one that has not finished
 * in a minute is not going to finish because this component asked again, and by then the worker
 * has written down why and the panel is saying something actionable.
 */
const ATTEMPTS = 20;

export function Settlement({ showId }: { showId: string }) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<ShowOrderRow[]>();
  /** Written a reason for by the worker — somebody has to look (`R-SYNC-16`). */
  const [failed, setFailed] = useState(0);
  /** Not there yet, which is every order for the first seconds after a show ends. */
  const [settling, setSettling] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    try {
      const answer = await shows.orders(showId);
      setOrders(answer.orders);
      setFailed(answer.failed);
      setSettling(answer.settling);
      setError(undefined);
    } catch (failure) {
      setError(
        failure instanceof ApiError ? failure.message : t("settlement.loadFailed"),
      );
    }
  }, [showId, t]);

  /**
   * Read on mount, then **followed until it settles** — which is not the same as polled forever.
   *
   * Reading once and stopping looks sufficient, on the reasoning that *settlement finishes within
   * seconds of a show ending and then never changes again on its own*. Both halves are true and
   * the conclusion is still wrong, because of **when this component mounts**: the host ends the
   * show and looks at this panel in the same second, which is the one second in a show's life when
   * the answer is guaranteed to be about to change. Read once, with a worker mid-flight, the panel
   * says the customer's order has not reached Shopify — about an order that reaches it a moment
   * later, while the warning stays on screen until somebody reloads.
   *
   * So it follows the queue, and it stops as soon as there is nothing in it. `ATTEMPTS` is the
   * ceiling: settlement either completes in seconds or it has refused and written down why, and a
   * refusal is not something more polling will fix. After that the refresh button is the way, and
   * by then the panel is saying something a host can act on rather than something transient.
   */
  useEffect(() => {
    let alive = true;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function read(): void {
      void shows.orders(showId).then(
        (answer) => {
          if (!alive) return;
          setOrders(answer.orders);
          setFailed(answer.failed);
          setSettling(answer.settling);
          setError(undefined);
          // Only while something can still move, and only for as long as it is worth waiting.
          if (answer.settling > 0 && ++attempts < ATTEMPTS) {
            timer = setTimeout(read, FOLLOW_MS);
          }
        },
        (failure: unknown) => {
          if (!alive) return;
          setError(
            failure instanceof ApiError ? failure.message : t("settlement.loadFailed"),
          );
        },
      );
    }

    read();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [showId, t]);

  /**
   * The re-drive (`R-SYNC-16`'s *retried on failure*). It asks the queue and then re-reads, and
   * the re-read is deliberately immediate rather than delayed: settlement is a worker and a
   * Shopify round trip, so the table will usually still say *not settled* when it comes back.
   * That is honest — the button asked for it, it did not do it — and pressing refresh a moment
   * later is what shows the result.
   */
  const settle = useCallback(async () => {
    setBusy(true);
    try {
      const answer = await shows.settle(showId);
      setOrders(answer.orders);
      setFailed(answer.failed);
      setSettling(answer.settling);
      setError(undefined);
    } catch (failure) {
      setError(
        failure instanceof ApiError ? failure.message : t("settlement.settleFailed"),
      );
    } finally {
      setBusy(false);
    }
  }, [showId, t]);

  if (!orders) {
    return (
      <Paper withBorder radius="lg" p="lg" className={styles.card}>
        <Group justify="center">
          <Loader size="sm" color="ink" />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <Text fw={650}>{t("settlement.heading")}</Text>
          <Group gap="xs">
            <Button size="compact-xs" variant="default" onClick={() => void load()}>
              {t("settlement.refresh")}
            </Button>
            {/* Only for a refusal. A re-drive of something already in the queue achieves nothing. */}
            {failed > 0 ? (
              <Button
                size="compact-xs"
                variant="light"
                color="orange"
                loading={busy}
                onClick={() => void settle()}
              >
                {t("settlement.settleAgain")}
              </Button>
            ) : null}
          </Group>
        </Group>

        {orders.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("settlement.none")}
          </Text>
        ) : (
          <>
            {/*
              `R-SYNC-16` — said before the table rather than left to be counted from it, and in
              three sentences rather than two. *Still going* and *it refused* are different facts
              with different remedies, and saying the second when the first is true is how a host
              was told a paid customer had no order while the order was being created.
            */}
            {failed > 0 ? (
              <Alert color="orange" variant="light" radius="md">
                {t("settlement.outstanding", { count: failed })}
              </Alert>
            ) : settling > 0 ? (
              <Alert color="blue" variant="light" radius="md">
                <Group gap="xs" wrap="nowrap">
                  <Loader size="xs" color="blue" />
                  <span>{t("settlement.settling", { count: settling })}</span>
                </Group>
              </Alert>
            ) : (
              <Alert color="green" variant="light" radius="md">
                {t("settlement.allSettled", { count: orders.length })}
              </Alert>
            )}

            <Table.ScrollContainer minWidth={520}>
              <Table verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("settlement.customer")}</Table.Th>
                    <Table.Th>{t("settlement.items")}</Table.Th>
                    <Table.Th>{t("settlement.charged")}</Table.Th>
                    <Table.Th>{t("settlement.order")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr key={order.id}>
                      <Table.Td>
                        <Text size="sm">
                          {order.customer ?? t("settlement.unknown")}
                        </Text>
                        {/* Not repeated where the name already is the email. */}
                        {order.email && order.email !== order.customer ? (
                          <Text size="xs" c="dimmed">
                            {order.email}
                          </Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td>{order.items}</Table.Td>
                      {/* Pence, because `formatMoney` takes pence and the API answers decimals. */}
                      <Table.Td>
                        {formatMoney(Math.round(Number(order.charged) * 100))}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <StateBadge state={order.state} />
                          {/*
                            Shopify's own numeric id — the stored value is a
                            `gid://shopify/Order/123` and the tail is the only part a person can
                            use — linked to the order in the admin, in a new tab so the panel
                            stays where it was.
                          */}
                          {order.shopifyOrderId ? (
                            order.shopifyOrderUrl ? (
                              /* Ink, not the brand yellow, and underlined so it still reads as a link. */
                              <Anchor
                                href={order.shopifyOrderUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="xs"
                                c="ink.8"
                                underline="always"
                              >
                                {order.shopifyOrderId.split("/").at(-1)}
                              </Anchor>
                            ) : (
                              <Text size="xs" c="ink.8">
                                {order.shopifyOrderId.split("/").at(-1)}
                              </Text>
                            )
                          ) : null}
                          {/*
                            The problem verbatim, because the next step depends on which of
                            settlement's three refusals it is and a summarised version would lose
                            exactly the part that decides.
                          */}
                          {order.problem ? (
                            <Text size="xs" c="orange.8">
                              {order.problem}
                            </Text>
                          ) : null}
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}

        {error ? (
          <Alert color="red" variant="light" radius="md">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** The four states of a show order, spelled out rather than left as a column value. */
function StateBadge({ state }: { state: ShowOrderRow["state"] }) {
  const { t } = useTranslation();

  const colour =
    state === "completed"
      ? "green"
      : state === "cancelled"
        ? "gray"
        : state === "closed"
          ? "orange"
          : "blue";

  return (
    <Badge size="xs" variant="light" color={colour}>
      {t(`settlement.state.${state}`)}
    </Badge>
  );
}
